<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';
import { isUpgradePageBypassRoute } from 'dashboard/helper/routeHelpers';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import RadarPriorityPopup from 'dashboard/components-next/radar/RadarPriorityPopup.vue';
import CevicoCallPopup from 'dashboard/components-next/cevico/calls/CevicoCallPopup.vue';
import CevicoCallLock from 'dashboard/components-next/cevico/calls/CevicoCallLock.vue';
import NovaConversaModal from 'dashboard/components-next/cevico/conversas/NovaConversaModal.vue';
import BugReportDrawer from 'dashboard/components-next/radar/BugReportDrawer.vue';
import CevicoContextMenu from 'dashboard/components-next/cevico/CevicoContextMenu.vue';
import PatientChatHost from 'dashboard/components-next/cevico/PatientChatHost.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    RadarPriorityPopup,
    CevicoCallPopup,
    CevicoCallLock,
    NovaConversaModal,
    BugReportDrawer,
    CevicoContextMenu,
    PatientChatHost,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      // título que veio do servidor (nome da instalação) — é o padrão da aba.
      // 🏷️ item 345: "Chatwoot" de fábrica nunca vira título (14/10 a aba
      // voltou a mostrar Chatwoot em produção)
      baseTabTitle: /^(chatwoot)?$/i.test((document.title || '').trim())
        ? 'CEVICO S.Un'
        : document.title.trim(),
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    // Título da aba: na instalação da CEVICO (que também hospeda empresas
    // irmãs) segue a EMPRESA da conta aberta; numa VPS com marca própria
    // (MARCA=otica, life…) fica o nome do sistema dela (ex.: "ÓTICA S.Un").
    cevicoTabTitle() {
      const company = this.$store.getters['crm/getCompany'];
      const ownBrand = (window.cevicoMarca?.id || 'cevico') !== 'cevico';
      if (!ownBrand && company?.configured && company.name) return company.name;
      return this.baseTabTitle;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    isAccountPaywalled() {
      return this.upgradePageRef?.isAccountPaywalled;
    },
    bypassUpgradePage() {
      return isUpgradePageBypassRoute(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    cevicoTabTitle: {
      handler(title) {
        if (title) document.title = title;
      },
      immediate: true,
    },
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar :is-paywalled="isAccountPaywalled" />
        <RadarPriorityPopup />
        <CevicoCallPopup />
        <!-- 🔒 item 325: trava da ligação (tocando há N s / perdida até retornar) -->
        <CevicoCallLock />
        <!-- CEVICO 199: Nova conversa em 3 passos (abre por openNovaConversa) -->
        <NovaConversaModal />
        <BugReportDrawer />
        <!-- CEVICO 283: menu do botão direito (abre por openMenu / v-cv-menu) -->
        <CevicoContextMenu />
        <PatientChatHost />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <CommandBar :is-paywalled="isAccountPaywalled" />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
